import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function CompanyPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();

  const PHONE_NUMBER = '090-4277-4521';

  // トップページのお問い合わせフォームにスクロール
  const goToContact = () => {
    navigate('/', { state: { scrollTo: 'contact' } });
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/90 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 cursor-pointer">
              <img src="https://static.readdy.ai/image/f9c7a126b558a9be781ef4c29ae5dafc/8ebc0db39f9829cf06d07a5266fdf192.png" alt="Zenwest Logo" className="h-10 w-10 object-contain" />
              <div>
                <p className="text-2xl font-bold text-[#1A2332]">Zenwest</p>
                <p className="text-xs text-[#0EA5E9]">会計＆ITサポート</p>
              </div>
            </Link>
            <Link to="/" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#0EA5E9] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              トップページへ戻る
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Banner */}
      <div className="pt-24 pb-16 bg-gradient-to-br from-[#1A2332] to-[#1A2332]/90 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/900dd0fc0f17cf1017199bab1b5aac9c.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <p className="text-[#0EA5E9] text-sm font-semibold tracking-wider uppercase mb-3">About Us</p>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4">事業所概要</h1>
          <p className="text-white/70 text-lg">Zenwestについて</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-20">

        {/* Representative Message */}
        <section className="mb-20">
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-10 border border-gray-100">
            <div className="flex flex-col md:flex-row gap-10 items-start">
              <div className="shrink-0">
                <div className="w-32 h-32 rounded-2xl overflow-hidden bg-gray-200 border-4 border-white shadow-md">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_658f406a27b22d9b699915c1b91b7eb4_79e7f324.jpg"
                    alt="代表者 西村善克"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <p className="text-center text-sm text-gray-500 mt-2">西村 善克</p>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#0EA5E9]/10 rounded-full">
                    <i className="ri-double-quotes-l text-lg text-[#0EA5E9]"></i>
                  </div>
                  <h2 className="text-xl font-bold text-[#1A2332]">個人事業主の気持ちがわかるサポート</h2>
                </div>
                <blockquote className="text-gray-600 leading-relaxed text-[15px] space-y-3">
                  <p>
                    会計のプロとして、これまで多くの個人事業主・サロンオーナーのみなさんと向き合ってきました。
                    数字の相談はもちろん、「ひとりで全部やらなければならない」という重圧や、
                    「忙しくて後回しにしてしまう」という罪悪感——そんな声を何度もお聞きするうちに、
                    専門知識だけでなく、経営者の気持ちに寄り添うことこそが本当のサポートだと確信するようになりました。
                  </p>
                  <p>
                    サロンオーナーのみなさんには、お客様と向き合う大切な時間に集中していただきたい。
                    記帳・会計・パソコン周りのことは、安心してZenwestに丸ごとお任せください。
                    「お金のことで悩まなくていい毎日」を、一緒につくっていきましょう。
                  </p>
                </blockquote>
                <p className="mt-5 text-right text-sm font-semibold text-[#1A2332]">
                  Zenwest 代表　西村 善克
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Company Overview Table */}
        <section className="mb-20">
          <h2 className="text-2xl font-bold text-[#1A2332] mb-8 flex items-center gap-3">
            <span className="w-1.5 h-6 bg-[#0EA5E9] rounded-full inline-block"></span>
            事業所情報
          </h2>
          <div className="rounded-2xl border border-gray-100 overflow-hidden">
            {[
              { label: '事業所名', value: 'Zenwest（ゼンウェスト）' },
              { label: '事業内容', value: '記帳代行・クラウド会計サポート・IT導入支援・ホームページ制作' },
              { label: '対象業種', value: '美容室・ネイルサロン・エステサロン・個人事業主全般' },
              { label: '代表者', value: '西村 善克' },
              { label: '所在地', value: '大阪市西淀川区御幣島4-3-13' },
              { label: '電話番号', value: PHONE_NUMBER },
              { label: 'メール', value: 'お問い合わせフォームよりご連絡ください' },
              { label: '営業時間', value: '平日 9:00〜18:00（土日祝はご相談ください）' },
              { label: 'サービスエリア', value: '大阪府内を中心に対応（オンライン相談は全国対応）' },
              { label: '対応方法', value: 'お客様の店舗・事務所へのご訪問、コワーキングスペースでの対面、オンライン（Zoom・Google Meet）' },
            ].map((row, i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                <div className="w-36 md:w-48 shrink-0 px-6 py-4 text-sm font-semibold text-[#1A2332] border-r border-gray-100">
                  {row.label}
                </div>
                <div className="flex-1 px-6 py-4 text-sm text-gray-600">
                  {row.value}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">※ 事務所は自宅兼のため、ご来訪対応はしておりません。お客様のご都合に合わせた場所・方法でご対応いたします。</p>
        </section>

        {/* Services Overview */}
        <section className="mb-20">
          <h2 className="text-2xl font-bold text-[#1A2332] mb-8 flex items-center gap-3">
            <span className="w-1.5 h-6 bg-[#0EA5E9] rounded-full inline-block"></span>
            主なサービス
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { icon: 'ri-file-text-line', title: '記帳代行', desc: '領収書・レシートをまとめてお渡しいただくだけ。月次の帳簿付けをまるごと代行します。' },
              { icon: 'ri-discuss-line', title: '経営相談', desc: '数字の読み方や資金繰りのご不安まで、気軽に話せるパートナーとして寄り添います。年2回の定期相談も含まれます。' },
              { icon: 'ri-smartphone-line', title: 'クラウド会計サポート', desc: '弥生会計・freeeなど、スマホでできるお金の管理アプリの導入・操作をサポートします。' },
              { icon: 'ri-global-line', title: 'ホームページ制作', desc: 'サロンの魅力を伝えるシンプルで使いやすいホームページを制作します。' },
            ].map((s, i) => (
              <div key={i} className="flex gap-4 p-5 rounded-xl border border-gray-100 bg-white">
                <div className="w-10 h-10 flex items-center justify-center bg-[#0EA5E9]/10 rounded-lg shrink-0">
                  <i className={`${s.icon} text-xl text-[#0EA5E9]`}></i>
                </div>
                <div>
                  <p className="font-semibold text-[#1A2332] text-sm mb-1">{s.title}</p>
                  <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-gradient-to-r from-[#0EA5E9] to-[#0EA5E9]/80 rounded-3xl p-12">
          <h3 className="text-2xl font-bold text-white mb-4">まずは無料でご相談ください</h3>
          <p className="text-white/90 mb-8 text-sm">経理もパソコンも苦手でも大丈夫。お気軽にお声がけください。</p>
          <button
            onClick={goToContact}
            className="inline-block bg-white text-[#0EA5E9] font-bold px-10 py-4 rounded-full hover:bg-[#1A2332] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            お問い合わせはこちら
          </button>
        </section>
      </div>

      {/* Footer */}
      <footer className="bg-[#1A2332] text-white py-10 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <img src="https://static.readdy.ai/image/f9c7a126b558a9be781ef4c29ae5dafc/8ebc0db39f9829cf06d07a5266fdf192.png" alt="Zenwest Logo" className="h-8 w-8 object-contain" />
            <p className="text-xl font-bold">Zenwest</p>
          </div>
          <p className="text-sm text-gray-400 mb-4">美容室・ネイルサロン・エステサロン専門 会計＆ITサポート</p>
          <p className="text-xs text-gray-500">&copy; 2025 Zenwest. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
